import {useModal} from '@hooks';
import {useAppSelector} from '@lib/store';
import {IconButton} from '@molecules';
import {BookingLocationStatusCard} from '@organisms';
import {cn} from '@utils';
import React, {Dispatch, SetStateAction} from 'react';
import {useTranslation} from 'react-i18next';
import {ErrorObject} from '../types';
import {WorkdayStatus} from '@lib/store/src/data/timeslots/apiTypes';
import {IconStatusMapping} from './utils';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {useDispatch} from 'react-redux';
import {useEditWorkday} from './QuickOfficeDayButton_timeslots';

type Props = {
  disabled: boolean;
  date: string;

  processingFor: WorkdayStatus | null;
  setProcessingFor: Dispatch<SetStateAction<WorkdayStatus | null>>;

  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;

  errorObject: ErrorObject;
  setErrorObject: Dispatch<SetStateAction<ErrorObject>>;
};

export const QuickNotWorkingButton_timeslots = ({
  disabled,
  date: dateKey,
  loading,
  setLoading,
  errorObject,
  setErrorObject,
  processingFor,
  setProcessingFor,
}: Props) => {
  const forWorkdayStatus = 'NotWorking' satisfies WorkdayStatus;

  const {t} = useTranslation();
  const openEditWorkdayModal = useEditWorkday().openModal;
  const dispatch = useDispatch();

  // TODO: support multiple days in this UI
  const workday = useAppSelector((state) => {
    const entry = state.data.timeslots.workdaysByDate[dateKey];
    return entry?.data?.at(0) ?? null;
  });

  const createNotWorkingWorkday = async () => {
    setLoading(true);
    setErrorObject((p) => ({
      ...p,
      workday: null,
    }));
    setProcessingFor(forWorkdayStatus);

    try {
      await dispatch(
        actions.createWorkday({
          status: 'NotWorking',
          // TODO: support non full day workdays
          localStart: `${dateKey}T00:00`,
          localEnd: `${dateKey}T23:59`,
        }),
      );
    } catch (err) {
      alert(err);

      setErrorObject((p) => ({
        ...p,
        workday: t('workplace:CouldntFindASpot'),
      }));
    }

    setProcessingFor(null);
    setLoading(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    if (workday) {
      openEditWorkdayModal(workday, dateKey);
    } else {
      createNotWorkingWorkday();
    }
  };

  return (
    <>
      <IconButton
        className={cn('shrink-0', {
          'bg-focusing-brown-200': processingFor === forWorkdayStatus || workday?.status === forWorkdayStatus,
        })}
        disabled={disabled}
        iconButton="tertiary"
        icon={IconStatusMapping[forWorkdayStatus]}
        onClick={handleClick}
      />
      {!loading && workday && <p className="truncate leading-none text-mapiq-black-500">{t(`workplace:NotWorking`)}</p>}
    </>
  );
};
